import {useWindowedAudioData, visualizeAudio} from '@remotion/media-utils';
import React from 'react';
import {useCurrentFrame, useVideoConfig} from 'remotion';

const BAR_COUNT = 72;
const RADIUS = 182;

export const Spectrum: React.FC<{src: string; color: string}> = ({src, color}) => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();

	const {audioData, dataOffsetInSeconds} = useWindowedAudioData({
		src,
		fps,
		frame,
		windowInSeconds: 10,
	});

	if (!audioData) {
		return null;
	}

	const frequencyData = visualizeAudio({
		fps,
		frame,
		audioData,
		numberOfSamples: 128,
		optimizeFor: 'speed',
		dataOffsetInSeconds,
	});

	const half = frequencyData.slice(2, 2 + BAR_COUNT / 2);
	const loudest = Math.max(0.02, ...half);
	const normalized = half.map((value) => Math.min(1, value / loudest));
	const values = [...normalized.slice().reverse(), ...normalized];
	const pulse = frequencyData.slice(1, 22).reduce((sum, value) => sum + value, 0) / 21;

	return (
		<svg
			viewBox="-320 -320 640 640"
			style={{position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible'}}
		>
			<circle r={RADIUS - 22} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
			<circle
				r={RADIUS + 96}
				fill="none"
				stroke={color}
				strokeOpacity={0.18}
				strokeWidth={1}
				strokeDasharray="2 10"
				transform={`rotate(${frame * 0.2})`}
			/>
			{values.map((value, index) => {
				const angle = (index / BAR_COUNT) * Math.PI * 2 - Math.PI / 2;
				const length = 18 + Math.pow(value, 0.62) * 126;
				const inner = RADIUS + 8;
				const outer = inner + length;
				const opacity = 0.25 + Math.min(1, value) * 0.75;
				return (
					<line
						key={index}
						x1={Math.cos(angle) * inner}
						y1={Math.sin(angle) * inner}
						x2={Math.cos(angle) * outer}
						y2={Math.sin(angle) * outer}
						stroke={color}
						strokeWidth={5}
						strokeLinecap="round"
						strokeOpacity={opacity}
					/>
				);
			})}
			<circle
				r={RADIUS}
				fill="none"
				stroke={color}
				strokeOpacity={0.22 + pulse * 0.5}
				strokeWidth={2}
				style={{filter: `drop-shadow(0 0 ${24 + pulse * 60}px ${color})`}}
			/>
		</svg>
	);
};
